/**
 * DirectorySync — SCIM 2.0 provisioning admin panel.
 *
 * Allows admins to:
 *  - View provisioning status (last sync, user count, errors)
 *  - Generate / rotate bearer tokens for IdP (Azure AD, Okta, Google Workspace)
 *  - Copy SCIM endpoint URL
 *  - Revoke existing tokens
 *
 * Backend: POST/GET/DELETE /v1/scim/tokens (identity-service)
 */
import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  RefreshCw,
  Key,
  Trash2,
  Copy,
  CheckCircle,
  AlertCircle,
  Users,
  Clock,
  Plus,
  Eye,
  EyeOff,
  ExternalLink,
} from 'lucide-react';
import { api } from '../../lib/api';
import { scimEndpointUrl } from '../../lib/urls';
import { useToastStore } from '../../stores/toast';
import { useTranslation } from 'react-i18next';
import { tv, rich, relTime, uiLang } from './i18nUtil';

// ── Types ────────────────────────────────────────────────────────────────────

interface ScimToken {
  id: string;
  label: string;
  created_at: string;
  expires_at: string | null;
  last_used_at: string | null;
  created_by: string;
}

interface ScimStats {
  total_users: number;
  active_users: number;
  suspended_users: number;
  last_sync_at: string | null;
  provisioned_count: number;
  error_count: number;
}

interface CreateTokenPayload {
  label: string;
  expiresInDays?: number;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

// Absolute, and the app host's address even when shown in the admin portal (lib/urls.ts)
const SCIM_ENDPOINT = scimEndpointUrl();


// ── Sub-components ───────────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  icon: Icon,
  color = 'text-nord-4',
}: {
  label: string;
  value: string | number;
  icon: React.ElementType;
  color?: string;
}) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-nord-3/30 bg-nord-1/40 px-4 py-3">
      <Icon className={`h-5 w-5 shrink-0 ${color}`} aria-hidden="true" />
      <div>
        <p className="text-lg font-semibold text-nord-5">{value}</p>
        <p className="text-2xs text-nord-muted">{label}</p>
      </div>
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

export function DirectorySync() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const { addToast } = useToastStore();

  const [showCreate, setShowCreate] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [expireDays, setExpireDays] = useState<string>('365');
  const [newTokenValue, setNewTokenValue] = useState<string | null>(null);
  const [showToken, setShowToken] = useState(false);
  const [copiedEndpoint, setCopiedEndpoint] = useState(false);
  const [copiedToken, setCopiedToken] = useState(false);

  // ── Data fetching ──────────────────────────────────────────────────────────

  const { data: tokens = [], isLoading: loadingTokens } = useQuery<ScimToken[]>({
    queryKey: ['scim-tokens'],
    queryFn: async () => {
      const res = await api.get<ScimToken[]>('/v1/scim/tokens');
      return res.data ?? [];
    },
  });

  const { data: stats } = useQuery<ScimStats>({
    queryKey: ['scim-stats'],
    queryFn: async () => {
      // Admin-login stats route (the SCIM Users endpoint only accepts SCIM tokens)
      const res = await api.get<ScimStats>('/v1/scim/stats');
      return res.data ?? {
        total_users: 0, active_users: 0, suspended_users: 0, last_sync_at: null, provisioned_count: 0, error_count: 0,
      };
    },
    retry: false,
  });

  // ── Mutations ──────────────────────────────────────────────────────────────

  const createMut = useMutation({
    mutationFn: async (payload: CreateTokenPayload) => {
      const res = await api.post<{ token: string; id: string; label: string }>(
        '/v1/scim/tokens',
        payload,
      );
      return res;
    },
    onSuccess: (data) => {
      setNewTokenValue(data.data?.token ?? null);
      setShowCreate(false);
      setNewLabel('');
      setExpireDays('365');
      void qc.invalidateQueries({ queryKey: ['scim-tokens'] });
      addToast({ type: 'success', title: t('adminUi.directorySync.tokenCreated', 'Token created'), message: t('adminUi.directorySync.tokenCreatedMsg', "Copy it now — it won't be shown again") });
    },
    onError: () => addToast({ type: 'error', title: t('adminUi.error', 'Error'), message: t('adminUi.directorySync.createFailed', 'Failed to create token') }),
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/v1/scim/tokens/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['scim-tokens'] });
      addToast({ type: 'success', title: t('adminUi.directorySync.tokenRevoked', 'Token revoked'), message: t('adminUi.directorySync.tokenRevokedMsg', 'The provisioning token has been disabled') });
    },
    onError: () => addToast({ type: 'error', title: t('adminUi.error', 'Error'), message: t('adminUi.directorySync.revokeFailed', 'Failed to revoke token') }),
  });

  // ── Handlers ──────────────────────────────────────────────────────────────

  const copyEndpoint = useCallback(() => {
    void navigator.clipboard.writeText(SCIM_ENDPOINT);
    setCopiedEndpoint(true);
    setTimeout(() => setCopiedEndpoint(false), 2000);
  }, []);

  const copyNewToken = useCallback(() => {
    if (!newTokenValue) return;
    void navigator.clipboard.writeText(newTokenValue);
    setCopiedToken(true);
    setTimeout(() => setCopiedToken(false), 2000);
  }, [newTokenValue]);

  const handleCreate = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!newLabel.trim()) return;
      createMut.mutate({
        label: newLabel.trim(),
        expiresInDays: expireDays ? parseInt(expireDays, 10) : undefined,
      });
    },
    [newLabel, expireDays, createMut],
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6" aria-label={t('adminUi.directorySync.aria', 'Directory Sync settings')}>
      {/* Header */}
      <div>
        <h2 className="text-base font-semibold text-nord-5">{t('adminUi.directorySync.title', 'Directory Sync (SCIM 2.0)')}</h2>
        <p className="mt-1 text-xs text-nord-muted">
          {t('adminUi.directorySync.intro', 'Automatically provision and deprovision users from Azure AD, Okta, or Google Workspace using SCIM 2.0.')}{' '}
          <a
            href="https://docs.eurion.se/integrations/scim"
            target="_blank"
            rel="noopener noreferrer"
            className="text-frost-2 hover:underline inline-flex items-center gap-0.5"
          >
            {t('adminUi.directorySync.docs', 'Documentation')} <ExternalLink className="h-3 w-3" />
          </a>
        </p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <StatCard
            label={t('adminUi.directorySync.totalProvisioned', 'Total provisioned')}
            value={stats.total_users}
            icon={Users}
            color="text-frost-2"
          />
          <StatCard
            label={t('adminUi.directorySync.lastSync', 'Last sync')}
            value={relTime(t, stats.last_sync_at)}
            icon={Clock}
            color="text-aurora-3"
          />
          <StatCard
            label={t('adminUi.directorySync.syncErrors', 'Sync errors')}
            value={stats.error_count}
            icon={stats.error_count > 0 ? AlertCircle : CheckCircle}
            color={stats.error_count > 0 ? 'text-nord-11' : 'text-aurora-3'}
          />
        </div>
      )}

      {/* SCIM Endpoint */}
      <div>
        <p className="mb-1.5 text-xs font-medium text-nord-muted uppercase tracking-wide">
          {t('adminUi.directorySync.endpointUrl', 'SCIM Endpoint URL')}
        </p>
        <div className="flex items-center gap-2 rounded-lg border border-nord-3/40 bg-nord-0/60 px-3 py-2">
          <code className="flex-1 truncate text-xs font-mono text-nord-4" aria-label={t('adminUi.directorySync.endpointUrlAria', 'SCIM endpoint URL')}>
            {SCIM_ENDPOINT}
          </code>
          <button
            onClick={copyEndpoint}
            className="shrink-0 rounded p-1 hover:bg-nord-2 transition-colors"
            aria-label={copiedEndpoint ? t('adminUi.copied', 'Copied') : t('adminUi.directorySync.copyEndpointAria', 'Copy SCIM endpoint URL')}
            title={t('adminUi.directorySync.copyEndpoint', 'Copy endpoint URL')}
          >
            {copiedEndpoint ? (
              <CheckCircle className="h-3.5 w-3.5 text-aurora-3" />
            ) : (
              <Copy className="h-3.5 w-3.5 text-nord-muted" />
            )}
          </button>
        </div>
        <p className="mt-1 text-2xs text-nord-muted">
          {t('adminUi.directorySync.endpointHint', "Use this URL in your IdP's SCIM configuration. Authentication is via Bearer token below.")}
        </p>
      </div>

      {/* New token one-time display */}
      {newTokenValue && (
        <div
          className="rounded-xl border border-aurora-3/30 bg-aurora-3/5 p-4"
          role="alert"
          aria-live="assertive"
        >
          <p className="mb-2 text-sm font-semibold text-aurora-3 flex items-center gap-1.5">
            <Key className="h-4 w-4" /> {t('adminUi.directorySync.newToken', 'New bearer token — copy it now')}
          </p>
          <p className="mb-3 text-xs text-nord-muted">
            {rich(t('adminUi.directorySync.newTokenHint', 'This token will <b>not</b> be shown again. Store it securely in your IdP configuration.'))}
          </p>
          <div className="flex items-center gap-2 rounded-lg border border-nord-3/40 bg-nord-0/80 px-3 py-2">
            <code
              className="flex-1 truncate text-xs font-mono text-nord-4"
              aria-label={t('adminUi.directorySync.tokenValueAria', 'Bearer token value')}
            >
              {showToken ? newTokenValue : '•'.repeat(40)}
            </code>
            <button
              onClick={() => setShowToken((v) => !v)}
              className="shrink-0 rounded p-1 hover:bg-nord-2 transition-colors"
              aria-label={showToken ? t('adminUi.directorySync.hideToken', 'Hide token') : t('adminUi.directorySync.revealToken', 'Reveal token')}
            >
              {showToken ? (
                <EyeOff className="h-3.5 w-3.5 text-nord-muted" />
              ) : (
                <Eye className="h-3.5 w-3.5 text-nord-muted" />
              )}
            </button>
            <button
              onClick={copyNewToken}
              className="shrink-0 rounded p-1 hover:bg-nord-2 transition-colors"
              aria-label={copiedToken ? t('adminUi.copied', 'Copied') : t('adminUi.directorySync.copyToken', 'Copy token')}
            >
              {copiedToken ? (
                <CheckCircle className="h-3.5 w-3.5 text-aurora-3" />
              ) : (
                <Copy className="h-3.5 w-3.5 text-nord-muted" />
              )}
            </button>
          </div>
          <button
            onClick={() => { setNewTokenValue(null); setShowToken(false); }}
            className="mt-3 text-xs text-nord-muted hover:text-nord-4 transition-colors"
          >
            {t('adminUi.dismiss', 'Dismiss')}
          </button>
        </div>
      )}

      {/* Token list */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <p className="text-xs font-medium text-nord-muted uppercase tracking-wide">
            {t('adminUi.directorySync.tokensHeading', 'Provisioning Tokens')}
          </p>
          <button
            onClick={() => setShowCreate((v) => !v)}
            className="flex items-center gap-1 rounded-lg border border-nord-3/40 px-2.5 py-1 text-xs text-nord-4 hover:bg-nord-2 transition-colors"
            aria-expanded={showCreate}
            aria-controls="create-token-form"
          >
            <Plus className="h-3.5 w-3.5" />
            {t('adminUi.directorySync.newTokenButton', 'New token')}
          </button>
        </div>

        {/* Create form */}
        {showCreate && (
          <form
            id="create-token-form"
            onSubmit={handleCreate}
            className="mb-3 rounded-xl border border-nord-3/40 bg-nord-1/40 p-3 space-y-3"
            aria-label={t('adminUi.directorySync.createAria', 'Create provisioning token')}
          >
            <div>
              <label htmlFor="token-label" className="mb-1 block text-xs font-medium text-nord-4">
                {t('adminUi.directorySync.tokenLabel', 'Token label')} <span aria-hidden="true">*</span>
              </label>
              <input
                id="token-label"
                type="text"
                required
                aria-required="true"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder={t('adminUi.directorySync.tokenLabelPlaceholder', 'e.g. Azure AD production')}
                className="w-full rounded-lg border border-nord-3/40 bg-nord-0/60 px-3 py-1.5 text-sm text-nord-4 placeholder:text-nord-muted focus:border-frost-1 focus:outline-none focus:ring-1 focus:ring-frost-1"
              />
            </div>
            <div>
              <label htmlFor="token-expires" className="mb-1 block text-xs font-medium text-nord-4">
                {t('adminUi.directorySync.expiresIn', 'Expires in (days, blank = never)')}
              </label>
              <input
                id="token-expires"
                type="number"
                min={1}
                max={3650}
                value={expireDays}
                onChange={(e) => setExpireDays(e.target.value)}
                placeholder="365"
                className="w-full rounded-lg border border-nord-3/40 bg-nord-0/60 px-3 py-1.5 text-sm text-nord-4 placeholder:text-nord-muted focus:border-frost-1 focus:outline-none focus:ring-1 focus:ring-frost-1"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => { setShowCreate(false); setNewLabel(''); }}
                className="rounded-lg border border-nord-3/40 px-3 py-1.5 text-xs text-nord-4 hover:bg-nord-2 transition-colors"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                type="submit"
                disabled={createMut.isPending || !newLabel.trim()}
                className="flex items-center gap-1.5 rounded-lg bg-frost-1 px-3 py-1.5 text-xs font-medium text-white hover:bg-frost-2 disabled:opacity-50 transition-colors"
              >
                {createMut.isPending ? (
                  <RefreshCw className="h-3 w-3 animate-spin" aria-hidden="true" />
                ) : (
                  <Key className="h-3 w-3" aria-hidden="true" />
                )}
                {t('adminUi.directorySync.generate', 'Generate')}
              </button>
            </div>
          </form>
        )}

        {/* Token table */}
        {loadingTokens ? (
          <div className="space-y-2">
            {[1, 2].map((i) => (
              <div key={i} className="h-12 animate-pulse rounded-lg bg-nord-2/30" />
            ))}
          </div>
        ) : tokens.length === 0 ? (
          <div className="rounded-xl border border-dashed border-nord-3/40 py-8 text-center">
            <Key className="h-8 w-8 text-nord-3 mx-auto mb-2" aria-hidden="true" />
            <p className="text-sm text-nord-muted">{t('adminUi.directorySync.emptyTitle', 'No tokens yet')}</p>
            <p className="text-xs text-nord-muted mt-0.5">
              {t('adminUi.directorySync.emptyHint', 'Create a token to connect Azure AD, Okta, or Google Workspace')}
            </p>
          </div>
        ) : (
          <ul className="space-y-2" aria-label={t('adminUi.directorySync.tokensAria', 'Provisioning tokens')}>
            {tokens.map((token) => (
              <li
                key={token.id}
                className="flex items-center justify-between gap-3 rounded-xl border border-nord-3/30 bg-nord-1/40 px-4 py-3"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-nord-4">{token.label}</p>
                  <p className="text-2xs text-nord-muted">
                    {[
                      tv(t, 'adminUi.directorySync.created', 'Created {{when}}', { when: relTime(t, token.created_at) }),
                      token.last_used_at
                        ? tv(t, 'adminUi.directorySync.lastUsed', 'Last used {{when}}', { when: relTime(t, token.last_used_at) })
                        : t('adminUi.directorySync.neverUsed', 'Never used'),
                      token.expires_at
                        ? new Date(token.expires_at) < new Date()
                          ? t('adminUi.directorySync.expired', 'Expired')
                          : tv(t, 'adminUi.directorySync.expires', 'Expires {{when}}', {
                            when: new Date(token.expires_at).toLocaleDateString(uiLang(i18n), { year: 'numeric', month: 'short', day: 'numeric' }),
                          })
                        : t('adminUi.directorySync.noExpiry', 'No expiry'),
                    ].join(' · ')}
                  </p>
                </div>
                <button
                  onClick={() => {
                    if (confirm(tv(t, 'adminUi.directorySync.confirmRevoke', 'Revoke token "{{label}}"? This will break any IdP using it.', { label: token.label }))) {
                      deleteMut.mutate(token.id);
                    }
                  }}
                  disabled={deleteMut.isPending}
                  className="shrink-0 rounded p-1.5 text-nord-muted hover:text-nord-11 hover:bg-nord-11/10 transition-colors"
                  aria-label={tv(t, 'adminUi.directorySync.revokeAria', 'Revoke token {{label}}', { label: token.label })}
                >
                  <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Setup guide */}
      <details className="rounded-xl border border-nord-3/30 bg-nord-1/20 px-4 py-3">
        <summary className="cursor-pointer select-none text-xs font-medium text-nord-4 hover:text-nord-5 transition-colors">
          {t('adminUi.directorySync.guideTitle', 'Azure AD / Entra setup guide')}
        </summary>
        <ol className="mt-3 space-y-2 text-xs text-nord-muted list-decimal list-inside">
          <li>{rich(t('adminUi.directorySync.guide1', 'In Entra, go to <b>Enterprise Applications → New application → Non-gallery</b>'))}</li>
          <li>{rich(t('adminUi.directorySync.guide2', 'Navigate to <b>Provisioning → Provisioning Mode → Automatic</b>'))}</li>
          <li>{rich(t('adminUi.directorySync.guide3', 'Set <b>Tenant URL</b> to the SCIM endpoint above and paste your generated bearer token as <b>Secret Token</b>'))}</li>
          <li>{rich(t('adminUi.directorySync.guide4', 'Click <b>Test Connection</b> — should show "Credentials are valid"'))}</li>
          <li>{rich(t('adminUi.directorySync.guide5', 'Under <b>Mappings</b>, enable user and group sync'))}</li>
          <li>{rich(t('adminUi.directorySync.guide6', 'Set <b>Provisioning Status</b> to <b>On</b>'))}</li>
        </ol>
      </details>
    </div>
  );
}
